import { useEffect, useReducer, useState } from "react";
import { computeComplexType } from "../../utils";
import { BuyerSellerResponse, OrderToSubmitRequest } from "../../types";
import { SiteResponse } from "../../types/site.type";
import { complexTypes, initialState as defaultInitialState } from "./orderConstants";
import { generalBidReducer } from "./generalBidReducer";
import {
  applyInvite,
  selectAllJointSuppliers,
  selectAllSuppliers,
  toggleMaterial,
  toggleSupplier,
} from "./orderSupplierState";
import {
  addLineFile,
  applyUseOnceAddress as pureApplyUseOnceAddress,
  applyDatesToAll,
  assignSite,
  removeLineFile,
  setLineField,
  patchLine,
  setSimpleSupplyPeriod,
  setSupplyPeriod,
} from "./orderLineState";

/**
 * The cross-step state machine for BOTH buyer order-launch flows — the RFQ
 * builder (Order.tsx) and launch-from-PR (OrderByRequest.tsx). Owns
 * `ordersToSubmit` (the material/PR lines + their suppliers + per-line config)
 * and `generalBidData` (the reducer form) — the two inputs the payload builders
 * shape into the create/edit request. The container stays a thin orchestrator.
 *
 * Two options carry the only behavioural differences between the flows:
 *  - `initialState` — seed state differs (the RFQ builder seeds `internalCode`,
 *    the PR flow seeds `name`; see the constants' footgun note).
 *  - `resetAllSelectedOnInvite` — the PR flow clears the "all selected" toggle
 *    when inviting/adding a supplier (except the `clearAllForMaterial` path); the
 *    RFQ builder leaves it untouched.
 * Everything else (including the extra handlers one flow does not use) is exposed
 * to both — the unused ones are simply never wired up by that container.
 */
type UseOrderBuilderOptions = {
  initialState?: any;
  resetAllSelectedOnInvite?: boolean;
};

export const useOrderBuilder = ({
  initialState: seedState = defaultInitialState,
  resetAllSelectedOnInvite = false,
}: UseOrderBuilderOptions = {}) => {
  const [ordersToSubmit, setOrdersToSubmit] = useState<OrderToSubmitRequest[]>(
    []
  );
  const [generalBidData, setGeneralBidData] = useReducer(
    generalBidReducer,
    seedState
  );
  const [selectedType, setSelectedType] = useState<any>(null);
  const [selectedComplexType, setSelectedComplexType] = useState<any>(null);
  const [isAllSelected, setIsAllSelected] = useState(false);
  // Dead-but-preserved: only ever set to null in the original; kept so the
  // step-2 error slot renders identically (never).
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // single-vs-joint auto-routing (SSOT). No manual selector.
  useEffect(() => {
    setSelectedComplexType(
      computeComplexType(ordersToSubmit.length, complexTypes)
    );
  }, [ordersToSubmit.length]);

  const handleCheckboxChange = (material: any) =>
    setOrdersToSubmit((prev) => toggleMaterial(prev, material));

  const handleSupplierSelection = (
    supplier: BuyerSellerResponse,
    materialId: string
  ) => {
    setIsAllSelected(false);
    setOrdersToSubmit((prev) => toggleSupplier(prev, supplier, materialId));
  };

  const handleSelectAll = (suppliersData: any[]) => {
    if (!suppliersData) return;
    setOrdersToSubmit((prev) =>
      selectAllSuppliers(prev, suppliersData, isAllSelected)
    );
    setIsAllSelected((prev) => !prev);
  };

  const handleSelectJointAll = (suppliersData: Record<string, any[]>) => {
    if (!suppliersData) return;
    setOrdersToSubmit((prev) =>
      selectAllJointSuppliers(prev, suppliersData, isAllSelected)
    );
    setIsAllSelected((prev) => !prev);
  };

  const handleOrdersToSubmit = (invite: any, type?: any) => {
    if (resetAllSelectedOnInvite && type !== "clearAllForMaterial") {
      setIsAllSelected(false);
    }
    setOrdersToSubmit((prev) =>
      applyInvite(prev, invite, type, selectedComplexType)
    );
  };

  const assignSiteToLine = (lineId: string, site: SiteResponse | null) =>
    setOrdersToSubmit((prev) => assignSite(prev, lineId, site));

  // Launch-from-PR: a free-text delivery address for a line with no saved site.
  const handleAddressChange = (lineId: string, address: string) =>
    setOrdersToSubmit((prev) => setLineField(prev, lineId, "address", address));

  const applyUseOnceAddress = (lineId: string, address: string) =>
    setOrdersToSubmit((prev) => pureApplyUseOnceAddress(prev, lineId, address));

  const handleApplyDatesToAll = (source: any) =>
    setOrdersToSubmit((prev) => applyDatesToAll(prev, source));

  const handleAddLineFile = (lineId: string, file: any) =>
    setOrdersToSubmit((prev) => addLineFile(prev, lineId, file));

  const handleRemoveLineFile = (lineId: string, index: number) =>
    setOrdersToSubmit((prev) => removeLineFile(prev, lineId, index));

  const handleServiceDetailChange = (
    lineId: string,
    patch: Partial<OrderToSubmitRequest>
  ) => {
    setErrorMessage(null);
    setOrdersToSubmit((prev) => patchLine(prev, lineId, patch));
  };

  const handlePriceDecrementChanges = (
    e: React.ChangeEvent<HTMLInputElement>,
    material: any,
    key: any
  ) => {
    setErrorMessage(null);
    setOrdersToSubmit((prev) => setLineField(prev, material.id, key, e.target.value));
  };

  const handleSimpleSupplyPeriod = (
    e: React.ChangeEvent<HTMLInputElement>,
    matToBid: any,
    type: any
  ) =>
    setOrdersToSubmit((prev) =>
      setSimpleSupplyPeriod(prev, matToBid.id, e.target.value, type)
    );

  const handleSupplyPeriod = (
    e: React.ChangeEvent<HTMLInputElement>,
    matToBid: any,
    type: any
  ) =>
    setOrdersToSubmit((prev) =>
      setSupplyPeriod(prev, matToBid.id, e.target.value, type)
    );

  return {
    ordersToSubmit,
    setOrdersToSubmit,
    generalBidData,
    setGeneralBidData,
    selectedType,
    setSelectedType,
    selectedComplexType,
    setSelectedComplexType,
    isAllSelected,
    setIsAllSelected,
    errorMessage,
    handleCheckboxChange,
    handleSupplierSelection,
    handleSelectAll,
    handleSelectJointAll,
    handleOrdersToSubmit,
    assignSiteToLine,
    handleAddressChange,
    applyUseOnceAddress,
    handleApplyDatesToAll,
    handleAddLineFile,
    handleRemoveLineFile,
    handlePriceDecrementChanges,
    handleServiceDetailChange,
    handleSimpleSupplyPeriod,
    handleSupplyPeriod,
  };
};
